import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import { useJspVariables, useAppContext, useLoader, useDebugRender } from '../hooks';
import { IceIcon, PageContainer, PageHeader, TextInput } from '../components';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

const PageTfaEmailRegister = () => {
    useDebugRender('PageTfaEmailRegister');
    const navigate = useNavigate();
    const { jspVariables, launcher, panelStyle } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);
    const { showLoader, hideLoader } = useLoader();

    // Same source as release/26.2 — getTfaDetails stores the API payload here (including tfaEmail).
    // Navigation often happens before that async call finishes, so sync when Redux updates.
    const reduxUserTfaDetails = useSelector(state => state.tfaReducer.userTfaDetails) || {};
    const resolvedAppKey = useSelector(state => state.userInputsReducer.appKey);
    const savedAuthParam = useSelector(state => state.authenticateReducer.SAVED_AUTH_PARAM) || {};
    const userName = savedAuthParam.userName || reduxUserTfaDetails.user || '';
    const appKey = resolvedAppKey || savedAuthParam.appKey || reduxUserTfaDetails.app || '';
    const password = savedAuthParam.password || '';

    const [userTfaDetails, setUserTfaDetails] = useState(reduxUserTfaDetails);
    const [emailCodeSent, setEmailCodeSent] = useState(false);
    const [sendEmail, setSendEmail] = useState(true);
    const [confirmCode, setConfirmCode] = useState('');
    const [alert, setAlert] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (!reduxUserTfaDetails || Object.keys(reduxUserTfaDetails).length === 0) {
            return;
        }
        setUserTfaDetails(prev => {
            // Keep an in-progress edit; otherwise take the server-provided email.
            const keepLocalEmail = prev._emailEdited && prev.tfaEmail;
            return {
                ...reduxUserTfaDetails,
                tfaEmail: keepLocalEmail ? prev.tfaEmail : (reduxUserTfaDetails.tfaEmail || prev.tfaEmail || ''),
                _emailEdited: keepLocalEmail
            };
        });
    }, [reduxUserTfaDetails]);

    const onEmailChange = useCallback((event) => {
        let changedEmailValue = event.target.value;
        if (changedEmailValue) {
            changedEmailValue = changedEmailValue.trim();
        }
        setUserTfaDetails(prev => ({ ...prev, tfaEmail: changedEmailValue, _emailEdited: true }));
        if (fieldErrors.email) setFieldErrors(prev => { const next = { ...prev }; delete next.email; return next; });
    }, [fieldErrors.email]);

    const onConfirmCodeChange = useCallback((event) => {
        setConfirmCode(event.target.value);
        if (fieldErrors.confirmCode) setFieldErrors(prev => { const next = { ...prev }; delete next.confirmCode; return next; });
    }, [fieldErrors.confirmCode]);

    const sendEmailVerificationCode = useCallback(async (tfaDetails) => {
        const email = tfaDetails.tfaEmail || '';
        if (!email.trim() || !email.includes('@')) {
            setFieldErrors({ email: 'Valid email address is required' });
            return;
        }
        setFieldErrors({});
        setAlert(null);
        showLoader('Sending verification code...');
        try {
            await actions.emailVerificationCodeAsync({
                userName,
                password,
                appKey,
                tfaEmail: email,
                authenticated: false
            });
            setSendEmail(false);
            setEmailCodeSent(true);
        } catch (resp) {
            const message = Number(resp.code) === errorCodes.ERR_2FA_EMAIL_INVALID
                ? 'Invalid email address for 2FA.'
                : resp.message;
            setAlert({ message, severity: 'error' });
        } finally {
            hideLoader();
        }
    }, [userName, password, appKey, showLoader, hideLoader]);

    const verifyEmail = useCallback(async () => {
        if (!confirmCode || !confirmCode.trim()) {
            setFieldErrors({ confirmCode: 'Confirmation code is required' });
            return;
        }
        setFieldErrors({});
        setAlert(null);
        showLoader('Verifying code...');
        try {
            await actions.verifyEmailVerificationCodeAsync({
                userName,
                password,
                appKey,
                tfaEmail: userTfaDetails.tfaEmail,
                authenticated: false,
                verificationCode: confirmCode
            });
            navigate(constants.ROUTE_PAGE_TFA_REGISTRATION_DONE, { state: { action: 'TFA_REGISTERED' } });
        } catch (resp) {
            let message = resp.message;
            if (Number(resp.code) === errorCodes.ERR_EMAIL_VERIFICATION_CODE_INVALID) {
                message = 'Invalid verification code. Please try again.';
            } else if (Number(resp.code) === errorCodes.ERR_EMAIL_VERIFICATION_INVALID_SESSION) {
                message = 'Session expired. Please start over.';
            }
            setAlert({ message, severity: 'error' });
        } finally {
            hideLoader();
        }
    }, [userName, password, appKey, userTfaDetails, confirmCode, navigate, showLoader, hideLoader]);

    const isGlobalIdRegistration = userTfaDetails.app === constants.APP_KEY_GLOBAL;

    return (
        <PageContainer id="pageTfaEmailRegister" dataAutomationId="page_tfa_email_register">
            <Box sx={panelStyle}>
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="tfa_email_register_header">{isGlobalIdRegistration ? 'Global ID Registration' : getLabelFromJspVariables('title_tfa_registration')}</PageHeader>
                {(appKey && userName) &&
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1.5 }}
                        data-automation-id="tfa_email_register_account_context">
                        {isGlobalIdRegistration
                            ? <>Registering Global ID for <Box component="span" sx={{ fontWeight: 600 }}>{userName}</Box> ({appKey})</>
                            : <><Box component="span" sx={{ fontWeight: 600 }}>{userName}</Box> · {appKey}</>}
                    </Typography>
                }
                <Box>
                    {alert && <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}
                        id="pageTfaEmailRegisterAlert"
                        data-automation-id="page_tfa_email_register_alert" aria-live="polite">{alert.message}</Alert>}
                    {emailCodeSent && <Alert severity="success" aria-live="polite" sx={{ mb: 1 }}>{getLabelFromJspVariables('tfa_passcode_sent_to_email')}</Alert>}
                </Box>
                <Box>
                    <TextInput
                        id="tfar_email_reg"
                        dataAutomationId="tfar_email_reg"
                        name="tfaEmail"
                        label={getLabelFromJspVariables('label_email_required')}
                        type="email"
                        required
                        disabled={userTfaDetails.app == 'GLOBAL'}
                        error={!!fieldErrors.email}
                        helperText={fieldErrors.email}
                        value={userTfaDetails.tfaEmail || ''}
                        onChange={onEmailChange}
                    />
                    {userTfaDetails && userTfaDetails.RESTRICTED_EMAIL_DOMAINS &&
                        <Box id="restrictedEmailDomains_reg" data-automation-id="restricted_email_domains_reg">
                            <Typography variant="body2">{userTfaDetails.RESTRICTED_EMAIL_DOMAINS}</Typography>
                        </Box>
                    }
                </Box>
                {sendEmail ?
                    <Box>
                        <Button variant="contained" fullWidth onClick={() => sendEmailVerificationCode(userTfaDetails)}
                            id="btnEmailVerificationCodeReg"
                            data-automation-id="btn_email_verification_code_reg">
                            {getLabelFromJspVariables('send_confirmation_email')}
                        </Button>
                    </Box>
                    :
                    <Box>
                        <Button variant="contained" fullWidth onClick={() => sendEmailVerificationCode(userTfaDetails)}
                            id="btnEmailVerificationCodeReg"
                            data-automation-id="btn_email_verification_code_resend"
                            sx={{ mb: 1.5 }}>
                            {getLabelFromJspVariables('resend_confirmation_code')}
                        </Button>

                        <Box id="divEmailVerifyReg" data-automation-id="div_email_verify_reg">
                            <Alert severity="info" sx={{ mb: 1.5 }}>
                                {getLabelFromJspVariables('enter_email_confirmation_code')}
                            </Alert>
                            <Typography component="label" htmlFor="tfar_confirm_code" fontWeight={500}>
                                {getLabelFromJspVariables('label_email_confirmation')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <TextInput
                                        id="tfar_confirm_code"
                                        dataAutomationId="tfar_confirm_code"
                                        name="tfar_otpEmail"
                                        label={getLabelFromJspVariables('label_confirmation_code')}
                                        required
                                        autoComplete="one-time-code"
                                        numericOnly maxLength={6}
                                        error={!!fieldErrors.confirmCode}
                                        helperText={fieldErrors.confirmCode}
                                        value={confirmCode}
                                        onChange={onConfirmCodeChange} />
                                </Box>
                                <Button variant="contained" onClick={verifyEmail}
                                    id="btnEmailVerificationCodeReg"
                                    data-automation-id="btn_email_verification_code_confirm"
                                    sx={{ my: 1, flexShrink: 0 }}>
                                    {getLabelFromJspVariables('action_confirm')}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                }
            </Box>
        </PageContainer>
    );
};

export default PageTfaEmailRegister;
